import { useRef, useState } from 'react';
import { ArrowIcon, CheckIcon, MailIcon, PhoneIcon } from './components/Icons.js';

export const SUPPORT_EMAIL = 'support@foody.az';
export const SUPPORT_PHONE = '+994103663900';
export const SUPPORT_PHONE_LABEL = '+994 10 366 39 00';

const FIELDS = [
  { name: 'name', type: 'text', autoComplete: 'name', required: true, wide: true },
  { name: 'address', type: 'text', autoComplete: 'street-address', required: true, wide: true },
  { name: 'phone', type: 'tel', autoComplete: 'tel', inputMode: 'tel', required: true },
  { name: 'email', type: 'email', autoComplete: 'email', inputMode: 'email' },
  { name: 'message', multiline: true, wide: true },
];

const EMPTY = { name: '', address: '', phone: '', email: '', message: '' };
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (data) => {
  const invalid = {};
  if (!data.name.trim()) invalid.name = true;
  if (!data.address.trim()) invalid.address = true;
  if (data.phone.replace(/\D/g, '').length < 7) invalid.phone = true;
  if (data.email.trim() && !EMAIL_PATTERN.test(data.email.trim())) invalid.email = true;
  return invalid;
};

// There is no backend endpoint for restaurant applications yet, so the form
// hands a pre-filled email to the visitor's mail app instead of pretending to send.
const JoinForm = ({ currentText }) => {
  const [formData, setFormData] = useState(EMPTY);
  const [invalid, setInvalid] = useState({});
  const [showError, setShowError] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const formRef = useRef(null);

  const labels = currentText.joinFormPlaceholders;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (invalid[name]) {
      setInvalid((prev) => {
        const next = { ...prev };
        delete next[name];
        if (Object.keys(next).length === 0) setShowError(false);
        return next;
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextInvalid = validate(formData);
    setInvalid(nextInvalid);

    const firstInvalid = FIELDS.find((field) => nextInvalid[field.name]);
    if (firstInvalid) {
      setShowError(true);
      setIsShaking(true);
      formRef.current?.querySelector(`[name="${firstInvalid.name}"]`)?.focus();
      return;
    }

    const body = [
      `${labels[0]}: ${formData.name.trim()}`,
      `${labels[1]}: ${formData.address.trim()}`,
      `${labels[2]}: ${formData.phone.trim()}`,
      formData.email.trim() && `${labels[3]}: ${formData.email.trim()}`,
      formData.message.trim() && `\n${formData.message.trim()}`,
    ]
      .filter(Boolean)
      .join('\n');

    window.location.href =
      `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(currentText.formMailSubject)}` +
      `&body=${encodeURIComponent(body)}`;
    setShowError(false);
    setIsSent(true);
  };

  if (isSent) {
    return (
      <div className="join-card join-card--sent" role="status">
        <span className="join-sent__badge">
          <CheckIcon size={30} />
        </span>
        <h3 className="join-sent__title">{currentText.formSuccessTitle}</h3>
        <p className="join-sent__text">{currentText.formSuccessMessage}</p>
        <div className="join-sent__links">
          <a className="chip-link" href={`tel:${SUPPORT_PHONE}`}>
            <PhoneIcon size={18} /> {SUPPORT_PHONE_LABEL}
          </a>
          <a className="chip-link" href={`mailto:${SUPPORT_EMAIL}`}>
            <MailIcon size={18} /> {SUPPORT_EMAIL}
          </a>
        </div>
        <button type="button" className="text-button" onClick={() => setIsSent(false)}>
          {currentText.formBack}
        </button>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      className={`join-card${isShaking ? ' is-shaking' : ''}`}
      onSubmit={handleSubmit}
      onAnimationEnd={(event) => event.target === event.currentTarget && setIsShaking(false)}
      noValidate
    >
      <div className="join-card__grid">
        {FIELDS.map((field, index) => {
          const id = `join-${field.name}`;
          const common = {
            id,
            name: field.name,
            value: formData[field.name],
            onChange: handleChange,
            placeholder: ' ',
            className: 'field__control',
            'aria-invalid': invalid[field.name] ? true : undefined,
            'aria-required': field.required || undefined,
          };
          return (
            <div key={field.name} className={`field${field.wide ? ' field--wide' : ''}`}>
              {field.multiline ? (
                <textarea {...common} rows={4} />
              ) : (
                <input {...common} type={field.type} autoComplete={field.autoComplete} inputMode={field.inputMode} />
              )}
              <label htmlFor={id} className="field__label">
                {labels[index]}
                {field.required && <span className="field__required" aria-hidden="true"> *</span>}
              </label>
            </div>
          );
        })}
      </div>

      <div className="join-card__error" data-visible={showError} role="alert">
        {showError && currentText.formErrorMessage}
      </div>

      <button type="submit" className="button button--primary button--block">
        <span>{currentText.joinFormButton}</span>
        <ArrowIcon size={20} className="button__arrow" />
      </button>
    </form>
  );
};

export default JoinForm;
